import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Package, CreditCard, Receipt, ShoppingCart, DollarSign, ChevronUp, ChevronDown } from 'lucide-react';
import axios from 'axios';
import config from '../config';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

/**
 * Les achats e-commerce de Matomo, par article, catégorie ou référence.
 *
 * ## Pourquoi il ne passe pas par `RepartitionChart`
 *
 * Les cinq répartitions de ce dashboard parlent toutes le même vocabulaire — visites, rebond,
 * durée moyenne. Un article n'a ni l'un ni l'autre : il a une quantité vendue, des commandes, un
 * prix moyen. Les colonnes n'ont rien de commun, et les forcer dans le moule obligerait à rendre
 * une demi-douzaine de cases vides.
 *
 * ## Ce que Matomo rend, et ce qu'il ne rend pas
 *
 * Le composant équivalent de Google Analytics affiche « articles consultés » et « ajoutés au
 * panier ». Matomo n'a pas la première — son rapport d'articles ne connaît que ce qui a été
 * commandé — mais il a la seconde, sous une forme plus utile : les **paniers abandonnés**.
 *
 * C'est la colonne qui porte l'information de cet écran. Sur le compte de test, la formule phare
 * compte 591 achats pour **3 944 abandons** : un rapport de ventes seul ne l'aurait pas montré.
 */

/** Trois grains d'analyse, comme le sélecteur de Google Analytics en propose deux. */
const NIVEAUX = [
  { cle: 'name', libelle: "Nom de l'article" },
  { cle: 'category', libelle: 'Catégorie' },
  { cle: 'sku', libelle: 'Référence (SKU)' }
];

/*
 * Les colonnes, dans le gabarit de Google Analytics — mais avec les mesures que Matomo rend
 * vraiment. Pas d'« articles consultés » : son rapport d'articles ne connaît que ce qui a été
 * commandé. En revanche il a les paniers abandonnés, que GA4 n'expose pas.
 */
const COLONNES = [
  { cle: 'itemLabel', titre: 'Article', texte: true },
  { cle: 'quantity', titre: 'Quantité achetée', decimales: 0, icone: CreditCard, couleur: '#10b981', gras: '500' },
  { cle: 'orders', titre: 'Transactions', decimales: 0, icone: Receipt },
  { cle: 'avgPrice', titre: 'Prix moyen', decimales: 2, suffixe: '€' },
  { cle: 'avgQuantity', titre: 'Qté / transaction', decimales: 2 },
  { cle: 'abandonedQuantity', titre: 'Paniers abandonnés', decimales: 0, icone: ShoppingCart, tauxAbandon: true },
  { cle: 'revenue', titre: 'Revenu généré', decimales: 2, suffixe: '€', icone: DollarSign, couleur: '#059669', gras: '600', tiretSiZero: true }
];

/** L'intitulé de la première colonne suit le grain choisi. */
const LIBELLE_PREMIERE_COLONNE = {
  name: "Nom de l'article",
  category: 'Catégorie',
  sku: 'Référence'
};

const UNITE = { name: 'article', category: 'catégorie', sku: 'référence' };

/**
 * Les quatre chiffres de tête, comme la barre de totaux de Google Analytics.
 *
 * ⚠️ **« Revenu des articles » et non « Chiffre d'affaires »** — contrairement aux transactions,
 * ce n'est pas la même mesure sous deux noms. Le prix des articles d'un côté, le montant des
 * transactions de l'autre : 167 790,87 € contre 255 368,32 € sur trente jours. L'écart est
 * déclaré au niveau de la transaction et n'est porté par aucun article, d'où le bandeau qui
 * l'annonce. Les nommer pareil inviterait à les additionner ou à prendre l'un pour l'autre.
 */
const TOTAUX_ENTETE = [
  { cle: 'quantity', icone: CreditCard, libelle: 'Achetés :', couleur: '#10b981', decimales: 0 },
  { cle: 'orders', icone: Receipt, libelle: 'Transactions :', decimales: 0 },
  { cle: 'abandonedQuantity', icone: ShoppingCart, libelle: 'Abandonnés :', decimales: 0 },
  { cle: 'revenue', icone: DollarSign, libelle: 'Revenu des articles :', couleur: '#059669', decimales: 2, suffixe: '€' }
];

const CARTE = {
  marginTop: '24px',
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  overflow: 'hidden'
};

const BOUTON = (inactif) => ({
  padding: '6px 12px',
  fontSize: '0.85em',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  backgroundColor: inactif ? '#f3f4f6' : '#ffffff',
  color: inactif ? '#9ca3af' : '#374151',
  cursor: inactif ? 'not-allowed' : 'pointer',
  transition: 'all 0.15s'
});

const ITEMS_PER_PAGE = 10;

const MatomoEcommerceProducts = ({
  items = [],
  formatNumber,
  loading = false,
  /* De quoi recharger à un autre grain sans rejouer tout le dashboard. */
  token,
  siteId,
  period,
  customStartDate,
  customEndDate,
  accountId = 'global',
  userRole = 'USER',
  /* Les deux chiffres du site, qui ne se déduisent d'aucune somme de lignes. */
  commandesDuSite = 0,
  chiffreAffairesDuSite = 0,
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'matomo_ecommerce_products'
}) => {
  const [sortConfig, setSortConfig] = useState({ key: 'revenue', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);

  /*
   * Le grain d'analyse, et les lignes qu'il a rapportées.
   *
   * Le dashboard sert déjà le grain « article » dans sa charge initiale : `lignesChargees` ne
   * vaut quelque chose qu'après un changement, et c'est alors lui qui prime. Recharger tout le
   * dashboard pour un regroupement coûterait une quinzaine d'appels Matomo.
   */
  const [niveau, setNiveau] = useState('name');
  const [lignesChargees, setLignesChargees] = useState(null);
  const [chargement, setChargement] = useState(false);

  const changerNiveau = useCallback(async (nouveau) => {
    setNiveau(nouveau);
    if (nouveau === 'name') { setLignesChargees(null); return; }
    if (!token || !siteId) return;

    setChargement(true);
    try {
      const reponse = await axios.post(`${config.API_URL}/api/dashboard/matomo/ecommerce-items`,
        { siteId, period, customStartDate, customEndDate, niveau: nouveau },
        { headers: { Authorization: `Bearer ${token}` } });
      setLignesChargees(reponse.data?.data || []);
    } catch (erreur) {
      // Le tableau précédent vaut mieux qu'un écran vide : on garde ce qui est affiché.
      console.warn('[MATOMO-ACHATS] Changement de grain impossible :', erreur.message);
    } finally {
      setChargement(false);
    }
  }, [token, siteId, period, customStartDate, customEndDate]);

  // Un changement de période invalide le grain chargé : les lignes porteraient d'autres dates.
  useEffect(() => { setNiveau('name'); setLignesChargees(null); },
    [period, customStartDate, customEndDate, siteId]);

  const lignes = lignesChargees ?? items;


  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const triees = useMemo(() => {
    const copie = [...(lignes || [])];
    copie.sort((a, b) => {
      const va = a[sortConfig.key];
      const vb = b[sortConfig.key];
      if (typeof va === 'string') {
        return sortConfig.direction === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      return sortConfig.direction === 'asc' ? (va || 0) - (vb || 0) : (vb || 0) - (va || 0);
    });
    return copie;
  }, [lignes, sortConfig]);

  const totalPages = Math.ceil(triees.length / ITEMS_PER_PAGE);
  const affichees = useMemo(() => {
    const debut = (currentPage - 1) * ITEMS_PER_PAGE;
    return triees.slice(debut, debut + ITEMS_PER_PAGE);
  }, [triees, currentPage]);

  // Changer de tri ou de grain remet au début.
  useEffect(() => { setCurrentPage(1); }, [sortConfig, lignes]);

  /*
   * Les totaux somment **toutes** les lignes, jamais la page affichée — et les deux moyennes se
   * recalculent sur ces totaux. Un prix moyen de prix moyens donnerait autant de poids à un
   * article vendu une fois qu'à un autre vendu six cents.
   */
  const totaux = useMemo(() => {
    const cumul = (lignes || []).reduce((acc, l) => ({
      quantity: acc.quantity + (l.quantity || 0),
      orders: acc.orders + (l.orders || 0),
      revenue: acc.revenue + (l.revenue || 0),
      abandonedQuantity: acc.abandonedQuantity + (l.abandonedQuantity || 0)
    }), { quantity: 0, orders: 0, revenue: 0, abandonedQuantity: 0 });

    /*
     * **Les transactions ne s'additionnent pas d'une ligne à l'autre.**
     *
     * Une transaction qui porte trois articles différents est comptée dans les trois lignes.
     * Mesuré sur trente jours : 1 452 sommées pour 1 158 réelles, et les trois rapports — par
     * nom, par catégorie, par référence — donnent trois sommes différentes (1 452, 1 163, 1 470)
     * pour les mêmes commandes, selon le nombre de lignes qu'elles traversent.
     *
     * C'est exactement la métrique `transactions` de la carte Résultats — vérifié, les deux
     * valent `Goals.get(idGoal=ecommerceOrder).nb_conversions`, soit 1 158 pour 255 368,32 €.
     * Le compte du site est donc le seul juste, et il corrige du même coup la quantité par
     * transaction : 2,69 articles au lieu de 2,15.
     */
    const commandes = commandesDuSite > 0 ? commandesDuSite : cumul.orders;

    return {
      ...cumul,
      orders: commandes,
      avgPrice: cumul.quantity > 0 ? cumul.revenue / cumul.quantity : 0,
      avgQuantity: commandes > 0 ? cumul.quantity / commandes : 0
    };
  }, [lignes, commandesDuSite]);


  /*
   * Le taux d'abandon, badgé comme le taux panier→achat de Google Analytics.
   *
   * Les seuils sont inversés : là-bas un fort taux est bon, ici il signale ce qui se remplit
   * sans se commander. Les couleurs suivent le sens, pas la formule.
   */
  const tauxAbandon = (ligne) => {
    const total = (ligne.abandonedQuantity || 0) + (ligne.quantity || 0);
    if (total === 0) return null;
    return Math.round((ligne.abandonedQuantity || 0) / total * 100);
  };

  const SortIcon = ({ colonne }) => {
    if (sortConfig.key !== colonne) {
      return <ChevronDown size={14} style={{ opacity: 0.25 }} />;
    }
    return sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  const enTete = (colonne) => (
    <th
      key={colonne.cle}
      onClick={() => handleSort(colonne.cle)}
      style={{
        padding: '12px 16px',
        textAlign: colonne.texte ? 'left' : 'right',
        fontWeight: '600',
        color: '#374151',
        borderBottom: '1px solid #e5e7eb',
        cursor: 'pointer',
        userSelect: 'none'
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: colonne.texte ? 'flex-start' : 'flex-end',
        gap: '4px'
      }}>
        {colonne.icone && <colonne.icone size={14} />}
        {colonne.texte ? LIBELLE_PREMIERE_COLONNE[niveau] : colonne.titre}
        <SortIcon colonne={colonne.cle} />
      </div>
    </th>
  );

  const cellule = (ligne, colonne, pair) => {
    const base = {
      padding: '12px 16px',
      textAlign: colonne.texte ? 'left' : 'right',
      borderBottom: '1px solid #e5e7eb',
      color: colonne.couleur || '#4b5563',
      fontWeight: colonne.gras || 'normal'
    };

    if (colonne.texte) {
      return (
        <td
          key={colonne.cle}
          style={{ ...base, color: '#1f2937', fontWeight: '500', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          title={ligne[colonne.cle]}
        >
          {ligne[colonne.cle]}
        </td>
      );
    }

    const brut = ligne[colonne.cle] || 0;
    if (colonne.tiretSiZero && brut === 0) {
      return <td key={colonne.cle} style={base}><span style={{ color: '#9ca3af' }}>-</span></td>;
    }

    const taux = colonne.tauxAbandon ? tauxAbandon(ligne) : null;
    return (
      <td key={colonne.cle} style={base}>
        <span>{formatNumber(brut, colonne.decimales)}{colonne.suffixe || ''}</span>
        {taux !== null && taux > 0 && (
          <span style={{
            marginLeft: '8px',
            fontSize: '0.8em',
            color: taux >= 80 ? '#dc2626' : taux >= 50 ? '#d97706' : '#059669',
            backgroundColor: taux >= 80 ? '#fee2e2' : taux >= 50 ? '#fef3c7' : '#d1fae5',
            padding: '2px 6px',
            borderRadius: '10px'
          }}>
            {taux}%
          </span>
        )}
      </td>
    );
  };

  if (loading || chargement) {
    return (
      <div style={{ ...CARTE, padding: '40px', textAlign: 'center', color: '#6b7280' }}>
        Chargement des achats…
      </div>
    );
  }

  // Un site sans e-commerce ne doit pas afficher un cadre vide : le bloc disparaît.
  if (!lignes || lignes.length === 0) {
    return null;
  }

  const debut = (currentPage - 1) * ITEMS_PER_PAGE;
  const fin = Math.min(debut + ITEMS_PER_PAGE, triees.length);

  return (
    <div className="matomo-ecommerce-products" style={CARTE}>
      {/* En-tête */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '1.1em', fontWeight: '600', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Package size={20} style={{ color: '#1a1a1a' }} />
            Achats e-commerce
          </h3>

          {token && siteId && (
            <select
              value={niveau}
              onChange={(e) => changerNiveau(e.target.value)}
              style={{
                padding: '6px 12px',
                fontSize: '0.85em',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: '#ffffff',
                color: '#374151',
                cursor: 'pointer'
              }}
            >
              {NIVEAUX.map((n) => (
                <option key={n.cle} value={n.cle}>{n.libelle}</option>
              ))}
            </select>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ display: 'flex', gap: '24px', fontSize: '0.875rem', flexWrap: 'wrap' }}>
            {TOTAUX_ENTETE.map(({ cle, icone: Icone, libelle, couleur, decimales, suffixe }) => (
              <div key={cle} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Icone size={16} style={{ color: '#6b7280' }} />
                <span style={{ color: '#6b7280' }}>{libelle}</span>
                <strong style={{ color: couleur || '#1f2937' }}>
                  {formatNumber(totaux[cle] || 0, decimales)}{suffixe || ''}
                </strong>
              </div>
            ))}
          </div>
          {toggleClientModuleVisibility && (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Achats e-commerce"
            />
          )}
        </div>
      </div>

      {/*
        **L'écart entre le revenu des articles et le chiffre d'affaires, annoncé plutôt que tu.**

        Les deux chiffres ne mesurent pas la même chose : ici le prix des articles, ailleurs le
        montant des commandes. Mesuré sur trente jours, 167 790,87 € contre 255 368,32 € — un
        tiers d'écart, qui donnait au composant l'air d'en perdre une partie.

        Ce n'est pas une troncature : les trois rapports de Matomo — par nom, par catégorie, par
        référence — rendent tous la même somme à l'euro près, sur 3 118 articles pour les 3 179
        que le site déclare. L'écart est au niveau de la commande : frais, options, suppléments.
        Le site ne les ventile pas (`revenue_subtotal`, `revenue_tax`, `revenue_shipping` et
        `revenue_discount` valent tous zéro dans l'API), donc rien ne permet de les répartir
        entre les articles — et les inventer serait pire que l'écart.

        Le seuil de 1 % évite d'afficher une mention pour un arrondi.
      */}
      {chiffreAffairesDuSite > 0
        && Math.abs(chiffreAffairesDuSite - totaux.revenue) / chiffreAffairesDuSite > 0.01 && (
        <div style={{
          padding: '10px 20px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#fffbeb',
          fontSize: '0.8rem',
          color: '#92400e',
          lineHeight: 1.5
        }}>
          Revenu des articles. Le chiffre d'affaires des transactions atteint{' '}
          <strong>{formatNumber(chiffreAffairesDuSite, 2)}€</strong> : l'écart de{' '}
          <strong>{formatNumber(chiffreAffairesDuSite - totaux.revenue, 2)}€</strong> est déclaré au
          niveau de la transaction — frais, options, suppléments — et n'est porté par aucun article.
        </div>
      )}

      {/* Tableau */}
      <div style={{ overflowX: 'auto' }}>
        {/*
          La classe n'a aucune règle propre : elle fait entrer ce tableau, construit en styles
          en ligne, dans les règles communes de `table[class*="-table"]`.
        */}
        <table className="matomo-products-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
          <thead>
            <tr style={{ backgroundColor: '#f9fafb' }}>
              {COLONNES.map(enTete)}
            </tr>
          </thead>
          <tbody>
            {affichees.map((ligne, index) => (
              <tr
                key={ligne.item || index}
                style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb', transition: 'background-color 0.15s' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9fafb'; }}
              >
                {COLONNES.map((colonne) => cellule(ligne, colonne, index % 2 === 0))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pied : décompte et pagination */}
      <div style={{
        padding: '12px 16px',
        backgroundColor: '#f9fafb',
        borderTop: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div style={{ fontSize: '0.85em', color: '#6b7280' }}>
          Affichage {debut + 1}-{fin} sur {triees.length} {UNITE[niveau]}{triees.length > 1 ? 's' : ''}
        </div>

        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              disabled={currentPage === 1}
              style={BOUTON(currentPage === 1)}
            >
              ← Précédent
            </button>
            <span style={{ fontSize: '0.85em', color: '#6b7280', padding: '0 8px' }}>
              Page {currentPage} sur {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
              disabled={currentPage === totalPages}
              style={BOUTON(currentPage === totalPages)}
            >
              Suivant →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MatomoEcommerceProducts;
